import { createClient } from '@supabase/supabase-js'
import { TERMS_VERSION } from '../config/legal-documents'

const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!import.meta.env.VITE_SUPABASE_URL || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]', '::1'])

/**
 * Podmienia petle zwrotna w adresie Supabase na host, z ktorego wczytano strone.
 *
 * Lokalny Supabase stoi pod `http://127.0.0.1:54321` i tak ma go w `.env` kazdy dewelop. Ale
 * `127.0.0.1` w przegladarce oznacza URZADZENIE ODWIEDZAJACEGO — przy wejsciu na dev-serwer
 * z innej maszyny (telefon, laptop w LAN-ie, praca na zdalnym hoście) logowanie strzela w pustke
 * i wisi do timeoutu. Ta sama pulapka co adres bezwzgledny w services/api.ts.
 *
 * Podmieniamy WYLACZNIE petle zwrotna i wylacznie wtedy, gdy sama strona nie stoi na petli —
 * adres produkcyjny (*.supabase.co) i praca na localhoscie zostaja nietkniete.
 */
function resolveSupabaseUrl(configured: string): string {
  if (typeof window === 'undefined') return configured
  try {
    const url = new URL(configured)
    if (!LOOPBACK.has(url.hostname) || LOOPBACK.has(window.location.hostname)) return configured
    url.hostname = window.location.hostname
    return url.toString().replace(/\/$/, '')
  } catch {
    return configured
  }
}

export const supabase = createClient(resolveSupabaseUrl(import.meta.env.VITE_SUPABASE_URL), supabaseAnonKey)

interface SignUpResult {
  needsEmailVerification: boolean;
  email: string | undefined;
}

// Auth helper functions
export async function signUp(email: string, password: string): Promise<SignUpResult> {
  const invitedBy = sessionStorage.getItem('invitedBy')
  // Wersja regulaminu idzie w metadanych uzytkownika, bo trigger handle_new_user tworzy profil
  // ZANIM aplikacja zobaczy nowego uzytkownika (rejestracja mailem konczy sie potwierdzeniem
  // adresu, wiec nie ma momentu, w ktorym moglibysmy dopisac akceptacje z zalogowanej sesji).
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        terms_version: TERMS_VERSION,
        ...(invitedBy ? { invitedBy } : {}),
      }
    }
  })
  if (error) throw error
  
  // Clear the invitation code after successful signup
  sessionStorage.removeItem('invitedBy')
  
  return {
    needsEmailVerification: true,
    email: data.user?.email
  }
}

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  if (error) throw error
}

export async function signOut() {
  // scope is important, using 'global' breaks parallel tests!
  const { error } = await supabase.auth.signOut({"scope": "local"})
  if (error) throw error
}
